/**
 * The HOC to inject the ApolloQuery result into a component's props.
 *
 * Implementation examples can be found in the tests as follows:
 * - Default use (wrap one query on a component):
 *     ./with-gql-query_test.js
 * - Wrap multiple queries on a component while avoiding prop name
 *   collisions:
 *     ./with-gql-query-multiple_test.js
 */
import * as React from "react";

import type {
    ApolloQueryOptions,
    AsyncApolloQueryResult,
} from "@khan/data-gql-apollo";
import type {GqlOperation} from "@khan/data-gql-core";

import ApolloQuery from "./apollo-query.tsx";

/**
 * Props for the simple use case of one query on a component.
 */
export type WithSingleApolloQueryProps<
    TData,
    TVariables extends Record<any, any> = any,
> = {
    gqlResult: AsyncApolloQueryResult<TData, TVariables>;
};

/**
 * When a component is using this HOC to add multiple cached queries and queries
 * then the props for the component must be built from this building block type
 * and the names that correspond the the configured
 * `mapResultToProps` methods, which attach each result to the relevant
 * injected properties.
 */
export type {AsyncApolloQueryResult};

/**
 * Type to strip specific cached query's parameters from a component's props.
 * Useful to strongly type the export of a component that was wrapped with
 * withApolloFastlyQuery.
 */
export type WithoutApolloQuery<TProps, TMappedProps> = Omit<
    TProps,
    keyof TMappedProps
>;

type Options<TData, TVariables extends object, TMappedProps> = {
    query: GqlOperation<TData, TVariables>;
    mapResultToProps?: (
        result: AsyncApolloQueryResult<TData, TVariables>,
    ) => TMappedProps;
} & ApolloQueryOptions<TVariables>;

const withApolloQuery =
    <TMappedProps, TData extends object, TVariables extends object = object>(
        options:
            | Options<TData, TVariables, TMappedProps>
            | ((props: any) => Options<TData, TVariables, TMappedProps>),
    ) =>
    <
        C extends React.ComponentType<
            React.ComponentProps<C> & TMappedProps
        > & {
            displayName?: string | undefined;
        },
    >(
        WrappedComponent: C,
    ) => {
        type OuterProps = Omit<
            JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
            keyof TMappedProps
        >;

        const ComponentWithApolloQuery = (props: OuterProps) => {
            const queryOptions =
                typeof options === "function" ? options(props) : options;
            const {mapResultToProps = defaultMapResultToProps, ...queryProps} =
                queryOptions;

            return (
                <ApolloQuery {...queryProps}>
                    {(result: AsyncApolloQueryResult<TData, TVariables>) => (
                        <WrappedComponent
                            {...(props as any)}
                            {...mapResultToProps(result)}
                        />
                    )}
                </ApolloQuery>
            );
        };

        // Try to create a nice displayName for React Dev Tools.
        const displayName =
            WrappedComponent.displayName ||
            WrappedComponent.name ||
            "Component";

        ComponentWithApolloQuery.displayName = `withApolloQuery(${displayName})`;

        return ComponentWithApolloQuery;
    };

/**
 * Default map function used when `mapResultToProps` is not provided.
 */
const defaultMapResultToProps = <TData, TVariables extends object>(
    result: AsyncApolloQueryResult<TData, TVariables>,
): WithSingleApolloQueryProps<TData, TVariables> => ({
    gqlResult: result,
});

export default withApolloQuery;
